import React, { useEffect, useState } from "react";
import { ColorsV2 } from "app/constants/color";
import {
  Radar, RadarChart, PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
  PolarRadiusAxis,
  Legend,
} from "recharts";
import apiService from "app/services/apiService";
import { useTranslation } from "react-i18next";
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import { Divider, IconButton, Stack, Typography } from "@mui/material";
import { Buttons } from "app/constants/sizing";

const GapRadarChart = ({ fwId, customOnClick = () => {} }) => {

  const { t } = useTranslation('translation', { keyPrefix: 'gap_analysis' });
  const language = JSON.parse(localStorage.getItem('lang'));
  const loadingText = t('loading');

  const DEFAULT_DATA = [
    { name: loadingText, value: 100, fullMark: 100, id: 1},
    { name: loadingText, value: 100, fullMark: 100, id: 2},
    { name: loadingText, value: 100, fullMark: 100, id: 3},
    { name: loadingText, value: 100, fullMark: 100, id: 4},
    { name: loadingText, value: 100, fullMark: 100, id: 5},
    { name: loadingText, value: 100, fullMark: 100, id: 6},
  ];

  // Navigation-related variables
  const [defaultNavigationStep, setDefaultNavigationStep] = useState(10);
  const [firstBarInView, setFirstBarInView] = useState(0);
  const [lastBarInView, setlastBarInView] = useState(defaultNavigationStep);

  const [data, setData] = useState(DEFAULT_DATA);
  // Data sorting:
  //    Cut off array to only show a max of defaultNavigationStep number of entries at a time
  //    Sort high to low/low to high -- NOT IMPLEMENTED
  const [sortedData, setSortedData] = useState(DEFAULT_DATA);

  const [isLoading, setIsLoading] = useState(true);
  const [chartColor, setChartColor] = useState(ColorsV2.outline);


  // TODO: ADD DOCUMENTATION
  const fetchChartData = async () => {
    try {
      const fetchStats = apiService.fetchFrameworksStats(language);
      const fetchSections = apiService.getFramworkAnswersSummary(fwId, language);
  
      const [statsRes, sectionsRes] = await Promise.all([fetchStats, fetchSections]);

      const  rawSections = statsRes?.data?.frameworks?.find(f => f.id === fwId)?.sections;
      const  rawLevels = sectionsRes?.data?.data;
      
      // Guard against null data or non-array data
      const sections = Array.isArray(rawSections) ? rawSections : [];
      const levels = Array.isArray(rawLevels) ? rawLevels : [];      

      const statsData = sections?.map((section) => {
        const totalQuestions = section?.nrOfQuestions || 1;
        return {
          name: section?.name,
          value: (section?.nrOfAnswered / totalQuestions) * 100,
          fullMark: 100,
          id: section?.id,
        };
      }) || [];

      const newData = statsData.map((item) => {
        const matchingSection = levels.find(levelItem => levelItem.id === item.id);
        return { 
          ...item, 
          level: matchingSection?.level ?? null 
        };
      });
      setData(newData);
      
      const newNavStep = Math.min(defaultNavigationStep, newData.length)
      setDefaultNavigationStep(newNavStep);

      // const newSortedData = newData;
      // newSortedData.length = Math.min(newSortedData.length, MAX_SUBJECTS);
      // setSortedData(newSortedData);
    }
    catch (error) {
      console.error(error);
    }
    finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setSortedData(DEFAULT_DATA);
    setIsLoading(true);
    fetchChartData();
  }, [fwId]);

  useEffect(() => {
    setChartColor(isLoading ? ColorsV2.outline : ColorsV2.acent);
  }, [isLoading]);

  useEffect(() => {
    setFirstBarInView(0)
    setlastBarInView(defaultNavigationStep);
  }, [defaultNavigationStep]);

  useEffect(() => {
    const newSortedData = data.slice(firstBarInView, lastBarInView);
    setSortedData(newSortedData);
  }, [firstBarInView, lastBarInView, data])

  const handleNavigation = direction => {
    let step = 0;

    // Check for chart bounds and make sure we don't navigate further than the chart allows
    if (direction === -1 && firstBarInView - defaultNavigationStep < 0) {
      step = firstBarInView;
    } else if (direction === 1 && lastBarInView + defaultNavigationStep >= data.length) {
      step =
        lastBarInView >= data.length ? 0 : data.length - lastBarInView;
    } else {
      step = defaultNavigationStep;
    }
    setlastBarInView(prev => prev + step * direction);
    setFirstBarInView(prev => prev + step * direction);
  };
  
  return (
    <>
      <style>{`
        .cursor-pointer svg {
          cursor: pointer !important;
        }
      `}</style>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='center'
          gap={3}
          sx={{cursor: 'auto'}}
        >
          <IconButton
            onClick={() => handleNavigation(-1)}
            sx={{
              ...Buttons.baseStyle.iconButton,
            }}
            disabled={firstBarInView <= 0}
          >
            <KeyboardArrowLeftOutlinedIcon />
          </IconButton>

          {defaultNavigationStep === data.length ?
          <Typography sx={{ color: ColorsV2.textInfo }}>
            {t("radar_chart_navbar_navOff_1")} <span style={{color: ColorsV2.white}}>{t("radar_chart_navbar_navOff_2")} ({data.length})</span> {t("radar_chart_navbar_navOff_3")}
          </Typography>
          :
          <Typography sx={{ color: ColorsV2.textInfo }}>
            {t("radar_chart_navbar_navOn_1")} <span style={{color: ColorsV2.white}}>{firstBarInView + 1} {t("radar_chart_navbar_navOn_2")} {lastBarInView}</span> {t("radar_chart_navbar_navOn_3")} {data.length}
          </Typography>}
          <IconButton
            onClick={() => handleNavigation(1)}
            sx={{
              ...Buttons.baseStyle.iconButton,
            }}
            disabled={lastBarInView >= data.length}
          >
            <KeyboardArrowRightOutlinedIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ borderColor: ColorsV2.outline}}></Divider>
      <div className="cursor-pointer" onClick={customOnClick}>
        <ResponsiveContainer width="100%" height={400} isAnimationActive={false}>
          <RadarChart cx="50%" cy="50%" outerRadius="80%" data={ sortedData } isAnimationActive={false}>
          <PolarGrid
          radialLines={true}
          stroke={chartColor}
          // strokeDasharray="1 40" // optional for a dotted effect,
          isAnimationActive={false}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tick={({ payload, x, y, textAnchor }) => {
              if (payload.value !== 100) return null; // Hide all except 100%
              return (
                <text
                  x={x}
                  y={y}
                  textAnchor={textAnchor}
                  fill={ColorsV2.acent}
                  fontSize={12}
                  fontFamily="roboto"
                >
                  {payload.value}%
                </text>
              );
            }}
            stroke={chartColor}
          />
          <PolarAngleAxis
          dataKey="id"
          axisLine={{ stroke: chartColor }}
          isAnimationActive={false}
          tick={({ payload, x, y, textAnchor }) => {
            const angle = payload.coordinate;

            let dy = 0; // default offset
            if (angle > 80 && angle < 100) {
              dy -= 5;
            } else if (angle < -80 || angle > -100) {
              dy += 5;
            }

            return (
              <text
                x={x}
                y={y}
                textAnchor={textAnchor}
                fill={ColorsV2.textInfo}
                fontSize={14}
                fontFamily="roboto"
                dy={dy}
              >
                {sortedData.find(d => d.id === payload.value)?.name}
              </text>
            );
            }}
            />
            <Radar
            name='% Answered'
            isAnimationActive={!isLoading}
            dataKey="value"
            stroke={ColorsV2.textInfo} // outline color
            fill={ColorsV2.outline} // fill color of the shape
            fillOpacity={0.4} // how transparent it is
            width={'100%'}
            />
            <Radar
            name='% Compliance'
            isAnimationActive={!isLoading}
            dataKey="level"
            stroke={ColorsV2.white} // outline color
            fill={ColorsV2.acent} // fill color of the shape
            fillOpacity={0.4} // how transparent it is
            width={'100%'}
            />
            <Legend 
            layout="vertical" 
            verticalAlign="top" 
            align="left"
            content={({ payload }) => {
              return(
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {payload.map((entry, index) => (
                  <li key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      display: 'inline-block',
                      backgroundColor: entry.payload.fill,
                      border: `2px solid ${entry.payload.stroke}`,
                      marginRight: 8,
                      borderRadius: 2,
                    }}
                  />
                  <span style={{ color: entry.payload.fill, fontFamily: 'Roboto, sans-serif', fontSize: 14  }}>{entry.value}</span>
                </li>
                ))}
              </ul>
            )}}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </>
)
};

export default GapRadarChart;
